/*
 * @format
 */

import React from 'react';
import { View } from 'react-native';
import Svg, { Circle, G, Line, Path, Rect, Text } from 'react-native-svg';

import styles from './styles';
import { primary, white } from '../../colours';

/*
 * The furniture the Watch App help illustrations are drawn with: the frame they
 * sit in, and the labels, leaders and arrows that point at the watch. None of it
 * has a counterpart on the watch itself, so it is kept quieter than the face it
 * annotates.
 */
export const BOLD = 'Montserrat-Bold, Montserrat';
export const MEDIUM = 'Montserrat-Medium, Montserrat';
export const REGULAR = 'Montserrat-Regular, Montserrat';

const LABEL_SIZE = 16;
const LABEL_LINE_HEIGHT = 21;
const BODY_SIZE = 13;
const BODY_LINE_HEIGHT = 17;

const STROKE = 2;
const LEADER_DOT = 4;
export const TAP_RADIUS = 16;
// The shadow is offset down and to the right, so a leader arriving from that
// side has more than the fingertip's own radius to clear.
export const TAP_SHADOW_OFFSET = 6;
const HIGHLIGHT_ALPHA = 0.22;
const ARROW_HEAD = 13;
// Half the angle between the arrow's two wings, in radians.
const ARROW_SPREAD = 0.42;

/*
 * A square-or-taller illustration that fills the column it is given. The aspect
 * ratio comes from the viewBox, so a diagram can be as tall as it needs without
 * its contents distorting.
 */
export function DiagramFrame({ width, height, description, children }) {
  return (
    <View
      style={[styles.diagram, { aspectRatio: width / height }]}
      accessible
      accessibilityRole="image"
      accessibilityLabel={description}>
      <Svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        {children}
      </Svg>
    </View>
  );
}

/*
 * A caption: one or more heading lines, optionally followed by lighter body
 * lines. The lines are given individually rather than wrapped, so a caption
 * always breaks where the design breaks it. [bold] is for a heading that names
 * something the reader does — a gesture — rather than one that merely points
 * at a part of the screen.
 */
export function Label({ x, y, heading, body = [], anchor = 'start', bold }) {
  const headingLines = Array.isArray(heading) ? heading : [heading];
  const bodyBaseline = y + headingLines.length * LABEL_LINE_HEIGHT;

  return (
    <G>
      {headingLines.map((line, index) => (
        <Text
          key={`heading-${index}`}
          x={x}
          y={y + index * LABEL_LINE_HEIGHT}
          fill={white}
          fontFamily={bold ? BOLD : MEDIUM}
          fontSize={LABEL_SIZE}
          textAnchor={anchor}>
          {line}
        </Text>
      ))}
      {body.map((line, index) => (
        <Text
          key={`body-${index}`}
          x={x}
          y={bodyBaseline + index * BODY_LINE_HEIGHT}
          fill={white}
          fontFamily={REGULAR}
          fontSize={BODY_SIZE}
          textAnchor={anchor}>
          {line}
        </Text>
      ))}
    </G>
  );
}

/*
 * Where a leader aiming at [to] should actually stop: short of it by [by],
 * along the line it arrives on. A caption points at a fingertip or a control
 * rather than touching it, and the dot on its end would otherwise sit on top of
 * the thing being named.
 */
export function stopShortOf(from, to, by) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);

  return { x: to.x - (dx / length) * by, y: to.y - (dy / length) * by };
}

/* A line from a caption to the thing it names, dotted where it lands. */
export function Leader({ from, to }) {
  return (
    <G>
      <Line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke={white}
        strokeWidth={STROKE}
      />
      <Circle cx={to.x} cy={to.y} r={LEADER_DOT} fill={white} />
    </G>
  );
}

/*
 * A fingertip on the glass: a white disc with a shadow behind it, marking where
 * the reader is being told to press.
 */
export function TapDot({ x, y, radius = TAP_RADIUS }) {
  return (
    <G>
      <Circle
        cx={x + TAP_SHADOW_OFFSET}
        cy={y + TAP_SHADOW_OFFSET}
        r={radius}
        fill={primary(700)}
      />
      <Circle cx={x} cy={y} r={radius} fill={white} />
    </G>
  );
}

/*
 * The band laid across the row a tap lands on, so the target reads as a whole
 * control rather than just the point of the finger.
 */
export function HighlightBand({ x, y, width, height }) {
  return (
    <Rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={white}
      fillOpacity={HIGHLIGHT_ALPHA}
    />
  );
}

/* A line with an open head at [to], for showing which way a gesture goes. */
export function Arrow({ from, to }) {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const wing = spread =>
    `${to.x - ARROW_HEAD * Math.cos(angle + spread)} ${
      to.y - ARROW_HEAD * Math.sin(angle + spread)
    }`;

  return (
    <G>
      <Line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke={white}
        strokeWidth={STROKE}
      />
      <Path
        d={`M ${wing(ARROW_SPREAD)} L ${to.x} ${to.y} L ${wing(-ARROW_SPREAD)}`}
        stroke={white}
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </G>
  );
}
